import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";

export type EnablePushResult = "granted" | "denied" | "unsupported";

// Lazy-load the Firebase Messaging plugin only on native platforms.
// Importing it at module top-level forces Rollup to bundle the web
// implementation into the browser build, which fails to resolve
// `WebPlugin` from @capacitor/core in production builds.
async function getFirebaseMessaging() {
  // Use a variable + @vite-ignore so Rollup does not statically analyse or
  // bundle the native-only plugin into the web build.
  const id = "@capacitor-firebase/messaging";
  const mod = await import(/* @vite-ignore */ id);
  return mod.FirebaseMessaging;
}

/**
 * Requests OS push permission and registers for remote notifications via FCM.
 * Native-only; on web returns "unsupported".
 */
export async function enablePush(): Promise<EnablePushResult> {
  if (!Capacitor.isNativePlatform()) return "unsupported";
  try {
    const FirebaseMessaging = await getFirebaseMessaging();
    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await FirebaseMessaging.requestPermissions();
    }
    if (perm.receive !== "granted") return "denied";
    const { token } = await FirebaseMessaging.getToken();
    if (token) await upsertToken(token);
    return "granted";
  } catch (err) {
    console.error("[push] enable error", err);
    return "denied";
  }
}

/**
 * Removes this user's device tokens so the backend stops targeting them.
 */
export async function disablePush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from("device_push_tokens").delete().eq("user_id", user.id);
    }
    try {
      const FirebaseMessaging = await getFirebaseMessaging();
      await FirebaseMessaging.deleteToken();
    } catch (e) {
      console.warn("[push] deleteToken failed", e);
    }
  } catch (err) {
    console.error("[push] disable error", err);
  }
}

/**
 * Returns whether push is currently active for this device.
 */
export async function getPushPermissionState(): Promise<EnablePushResult> {
  if (!Capacitor.isNativePlatform()) return "unsupported";
  try {
    const FirebaseMessaging = await getFirebaseMessaging();
    const perm = await FirebaseMessaging.checkPermissions();
    return perm.receive === "granted" ? "granted" : "denied";
  } catch {
    return "denied";
  }
}

async function upsertToken(token: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const platform = Capacitor.getPlatform() as "ios" | "android";
    await supabase.from("device_push_tokens").upsert(
      {
        user_id: user.id,
        token,
        platform,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "token" },
    );
  } catch (err) {
    console.error("[push] register upsert failed", err);
  }
}

let listenersInitialised = false;

/**
 * Wires the token-refresh and tap-handler listeners once.
 */
export async function initPushListeners(navigate: (path: string) => void): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (listenersInitialised) return;
  listenersInitialised = true;

  const FirebaseMessaging = await getFirebaseMessaging();

  FirebaseMessaging.addListener("tokenReceived", async ({ token }) => {
    if (token) await upsertToken(token);
  });

  FirebaseMessaging.addListener("notificationActionPerformed", ({ notification }) => {
    const data = (notification as any)?.data ?? {};
    const courseId = data.courseId;
    if (courseId) navigate(`/app/course/${courseId}`);
  });
}
